import Button from '@/components/Button'
import GsuLoader from '@/components/GsuLoader'
import UserPreference from '@/components/UserPreference'
import { useAuth } from '@/context/auth'
import { upsertUserPreferences } from '@/lib/preferences'
import { UserPreference as UserPreferenceType } from '@/shared/api-generated-types'
import Modal from '@/widgets/Modal'
import { useEffect, useMemo, useState } from 'react'
import { gql, useQuery } from 'urql'

export const QUERY_GET_ALL_USER_PREFERENCES = gql`
  query getAllUserPreferences {
    getAllUserPreferences {
      id
      name
      description
      icon
      isSelected
    }
  }
`

interface Props {
  isOpen: boolean
  onClose: () => void
  onUpdate: () => void
}

const UserPreferencesModal = (props: Props): JSX.Element | null => {
  const {
    state: { user },
  } = useAuth()
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [initialSelected, setInitialSelected] = useState<string[]>([])

  const [{ data, fetching }] = useQuery<{
    getAllUserPreferences: UserPreferenceType[]
  }>({
    query: QUERY_GET_ALL_USER_PREFERENCES,
    pause: user === null,
  })

  useEffect(() => {
    if (fetching || !data?.getAllUserPreferences) return
    const selectedIds = data.getAllUserPreferences
      .filter((preference) => preference.isSelected)
      .map((preference) => preference.id)
    setSelected(selectedIds)
    setInitialSelected(selectedIds)
  }, [data, fetching])

  function handleOnSelect(id: string): void {
    if (selected.includes(id)) {
      setSelected((selected) => selected.filter((key) => key !== id))
    } else {
      setSelected((selected) => [...selected, id])
    }
  }

  async function handleOnSave(): Promise<void> {
    try {
      setLoading(true)
      const response = await upsertUserPreferences(selected)
      if (response === true) props.onUpdate()
    } catch (error) {
      console.error('error', error)
    } finally {
      setLoading(false)
      setInitialSelected(selected)
      props.onClose()
    }
  }

  const hasChanged = useMemo(() => {
    return selected.length !== initialSelected.length
  }, [initialSelected.length, selected.length])

  if (!user) return null

  return (
    <Modal
      className="!bg-white text-primary !w-screen !max-w-full !max-h-[95vh] !m-4 sm:!m-8 sm:!h-[836px] lg:!m-12 lg:!h-[664px] xxl:!m-16 xxl:!h-[772px] relative overflow-hidden !p-4 sm:!p-8 lg:!p-12 xxl:!p-16"
      isOpen={props.isOpen}
      isClosable={false}
    >
      {fetching ? (
        <GsuLoader />
      ) : (
        <>
          <div className="text-xl sm:text-2xl font-bold mt-4 my-2">What are your interests?</div>
          <div className="text-base mb-8">
            Select the topics that interest you so we can suggest classes you'll love.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 overflow-auto">
            {data?.getAllUserPreferences.map((preference) => (
              <UserPreference
                key={preference.id}
                {...preference}
                isSelected={selected.includes(preference.id)}
                onClick={handleOnSelect}
              />
            ))}
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none">
            <div className="absolute !bottom-4 !left-4 !right-4 sm:!bottom-8 sm:!right-8 sm:!left-auto lg:!bottom-12 lg:!right-12 xxl:!bottom-16 xxl:!right-16 pointer-events-auto">
              <Button
                label={hasChanged ? 'Done' : 'Skip'}
                loading={loading}
                onClick={hasChanged ? handleOnSave : props.onClose}
                className="w-full !max-w-full sm:!w-20"
              />
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default UserPreferencesModal
